import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { buildCapitalSummaryModel, buildCapitalMovementsModel } from './optionsPerformanceUtils';
import { formatLocalizedDate } from './optionsLocaleFormatting';
import { useOptionsLocale } from '../../context/OptionsLanguageContext';

// ─── Capital Summary Section ──────────────────────────────────────────────────
// Shows: Capital Movements list inside a compact, collapsible accordion card.
// Displayed ONLY in Performance. Dashboard remains summary-only.
// Single source of truth: capitalPeriodsAllData (range=all).

const MovementRow = ({ movement }) => {
  const dateStr = movement.timestamp
    ? formatLocalizedDate(movement.timestamp)
    : '—';
  const isInflow = Number(movement.amountNative ?? 0) >= 0;
  const amtClass = isInflow ? 'text-[#22C55E]' : 'text-[#EF4444]';

  return (
    <div
      className="flex items-start justify-between gap-4 py-2.5 border-b border-white/[0.05] last:border-0"
      data-testid="options-capital-movement-row"
    >
      <div className="flex flex-col gap-0.5 min-w-0">
        <span className="text-[11px] uppercase tracking-[0.15em] text-white/40">{dateStr}</span>
        <span className="text-xs text-white/70">{movement.typeLabel}</span>
        <span className="text-xs text-white/40">{movement.currency}</span>
      </div>
      <div className="text-right flex-shrink-0">
        <p className={`text-sm font-semibold tabular-nums ${amtClass}`} data-testid="options-capital-movement-amount">
          {movement.amountNativeFormatted || '—'}
        </p>
        {movement.amountUsdFormatted ? (
          <p className="text-xs text-white/45 mt-0.5">{movement.amountUsdFormatted}</p>
        ) : null}
      </div>
    </div>
  );
};

const OptionsCapitalSummarySection = ({ capitalPeriodsAllData, accountData, selectedCurrency, t = null }) => {
  const { isPortuguese } = useOptionsLocale();
  const [expanded, setExpanded] = useState(false);
  const summary = buildCapitalSummaryModel({ capitalPeriodsAllData, accountData, selectedCurrency, t });
  const { movements, empty: noMovements } = buildCapitalMovementsModel({ capitalPeriodsAllData, t });

  if (!summary) return null;

  const depositCount = summary.depositCount || 0;
  const withdrawalCount = summary.withdrawalCount || 0;
  const depositText = isPortuguese
    ? `${depositCount} ${depositCount === 1 ? 'depósito' : 'depósitos'}`
    : `${depositCount} ${depositCount === 1 ? 'deposit' : 'deposits'}`;
  const withdrawalText = isPortuguese
    ? `${withdrawalCount} ${withdrawalCount === 1 ? 'saque' : 'saques'}`
    : `${withdrawalCount} ${withdrawalCount === 1 ? 'withdrawal' : 'withdrawals'}`;

  const hasAportado = summary.depositTotalFormatted !== null;
  const totalAportadoText = hasAportado
    ? (isPortuguese
        ? `Total aportado: ${summary.depositTotalFormatted}`
        : `Total deposited: ${summary.depositTotalFormatted}`)
    : null;

  return (
    <div className="space-y-4" data-testid="options-capital-summary-section">
      {/* Capital Movements Collapsible Card */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-4" data-testid="options-capital-movements-card">
        <button
          type="button"
          className="flex w-full items-center justify-between gap-3 text-left"
          onClick={() => setExpanded((e) => !e)}
          data-testid="options-capital-movements-toggle"
        >
          <div className="space-y-1">
            <p className="text-[11px] uppercase tracking-[0.18em] text-white/40" data-typography="label">
              {summary.titles.capitalMovements}
            </p>
            <div className="flex flex-wrap items-center gap-1.5 text-xs text-white/60">
              <span>{depositText} · {withdrawalText}</span>
              {totalAportadoText && (
                <>
                  <span className="text-white/25">·</span>
                  <span className="font-semibold text-[#60A5FA] tabular-nums">{totalAportadoText}</span>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1.5 rounded-full border border-white/10 px-2.5 py-1">
            <span className="text-[10px] font-semibold text-white/45">
              {expanded
                ? (isPortuguese ? 'Ocultar ▲' : 'Hide ▲')
                : (isPortuguese ? 'Ver histórico ▼' : 'View history ▼')}
            </span>
          </div>
        </button>

        {expanded && (
          <div className="mt-4 border-t border-white/5 pt-3" data-testid="options-capital-movements-wrapper">
            {noMovements ? (
              <p className="text-xs text-white/40 py-2">{summary.titles.noCashFlows}</p>
            ) : (
              <div data-testid="options-capital-movements-list">
                {movements.map((m, i) => (
                  <MovementRow key={`${m.timestamp_ms}-${m.currency}-${i}`} movement={m} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default OptionsCapitalSummarySection;
